import { averageRange, toScale } from '../../../../lib/grading';
import type { CredentialProgress } from '../../../../lib/api/credential-progress.api';
import {
  formatGpa,
  formatPoints,
  graduationMismatch,
  groupByTerm,
  progressFor,
  shortfallOf,
  type RecordAudience,
} from './academic-record.model';

// ─── El transcript no oficial, armado para imprimir (puro, con test) ────────
// El documento solo dibuja: qué va, en qué orden y con qué palabras se decide
// aquí, con las mismas reglas que la pantalla (academic-record.model).
//
// LO QUE NUNCA VA EN EL PAPEL:
//   · un identificador: ni el heredado del sistema de origen (no se guarda) ni
//     el id interno de la persona. Solo el código de alumno (L-######);
//   · el aviso de fallecido: es un dato administrativo, no académico, y el papel
//     puede llegar a terceros. En pantalla sigue.
//
// LO QUE SÍ VA, AUNQUE INCOMODE: si el sistema de origen registra una
// graduación que la regla vigente no confirma, van los dos datos, en tono
// neutro. Es un borrador para revisar, no un certificado. SALVO cuando lo pide
// el alumno (`audience: 'student'`): a él no se le muestra esa comparación ni
// las anotaciones de origen, igual que en pantalla (`progressFor`).

/** `t` de i18next, reducido a lo que usa el armador (así el test no lo carga). */
export type Tr = (key: string, params?: Record<string, unknown>) => string;

export interface RecordPdfRow {
  code: string;
  course: string;
  mark: string;
  outcome: string;
  points: string;
  attempted: string;
  earned: string;
  note: string | null;
  repeat: boolean;
}

/** UNA tabla continua: el período es una fila de grupo, y debajo sus cursos,
 *  en el orden cronológico del servidor. Con datos reales casi cada período
 *  trae un solo curso: un bloque con encabezado por período dejaba 16 cursos en
 *  3 hojas. */
export type RecordPdfLine = { kind: 'term'; name: string } | ({ kind: 'course' } & RecordPdfRow);

export interface RecordPdfData {
  title: string;
  watermark: string;
  draftNotice: string;
  tenantName: string;
  logoUrl?: string;
  address?: string;
  issuedOn: string;
  student: { label: string; name: string; code?: string; program?: string };
  /** Encabezados CORTOS, de una palabra: en papel una columna angosta parte
   *  «Créditos intentados» en dos líneas. «Créditos» / «Units» agrupa a
   *  «Intentados» y «Ganados» en una fila de arriba. */
  columns: {
    course: string;
    mark: string;
    points: string;
    units: string;
    attempted: string;
    earned: string;
  };
  repeatLabel: string;
  table: RecordPdfLine[];
  emptyRecord?: string;
  inProgress?: { title: string; help: string; terms: Array<{ name: string; courses: string[] }> };
  totals: Array<{ label: string; value: string; help?: string }>;
  credentialsTitle: string;
  credentials: Array<{ name: string; status: string; lines: string[] }>;
  sourceGraduation?: { title: string; lines: string[] };
  pageLabel: (page: number, total: number) => string;
}

/** Un logo que react-pdf sabe dibujar: no SVG ni WEBP. El de documentos
 *  primero, después el general; si ninguno sirve, el nombre en texto. */
export function pickDocumentLogo(config: Record<string, unknown>): string | undefined {
  for (const candidate of [config['documentLogoUrl'], config['logoUrl']]) {
    if (typeof candidate !== 'string' || !candidate) continue;
    let path = candidate.split('?')[0] ?? '';
    try {
      path = decodeURIComponent(path);
    } catch {
      /* una URL mal codificada se mira tal cual */
    }
    if (/\.(svg|webp)$/i.test(path)) continue;
    return candidate;
  }
  return undefined;
}

/** La dirección del contacto del landing (no hay todavía un campo propio). */
export function documentAddress(config: Record<string, unknown>): string | undefined {
  const landing = (config['landing'] ?? {}) as Record<string, unknown>;
  const contact = (landing['contact'] ?? {}) as Record<string, unknown>;
  const parts = [contact['address'], contact['city']]
    .filter((p): p is string => typeof p === 'string' && p.trim() !== '')
    .map((p) => p.trim());
  return parts.length ? parts.join(', ') : undefined;
}

/** Los bloques que el papel no parte: cada fila sola, y la fila de un período
 *  pegada a su primer curso, para que nunca quede sola al pie de una hoja. */
export function unbreakableBlocks(table: readonly RecordPdfLine[]): RecordPdfLine[][] {
  const blocks: RecordPdfLine[][] = [];
  for (const line of table) {
    const last = blocks[blocks.length - 1];
    if (line.kind === 'course' && last?.length === 1 && last[0]!.kind === 'term') last.push(line);
    else blocks.push([line]);
  }
  return blocks;
}

export function buildRecordPdfData(input: {
  progress: CredentialProgress;
  tenant: { name: string; config: Record<string, unknown> };
  /** Nombres de los programas del alumno (sus matrículas), ya resueltos. */
  programs?: readonly string[];
  issuedOn: string;
  /** Fecha `YYYY-MM-DD` → texto en el idioma de la UI. */
  formatDay: (ymd: string) => string;
  tr: Tr;
  /** Quién lo pide. Por defecto, el personal (la ficha de la persona). */
  audience?: RecordAudience;
}): RecordPdfData {
  const { tenant, tr } = input;
  const audience = input.audience ?? 'staff';
  const p = progressFor(input.progress, audience);
  const student = audience === 'student';
  const logoUrl = pickDocumentLogo(tenant.config);
  const address = documentAddress(tenant.config);

  const table: RecordPdfLine[] = groupByTerm(p.rows).flatMap((g) => [
    { kind: 'term' as const, name: g.termName },
    ...g.rows.map((r) => ({
      kind: 'course' as const,
      code: r.courseCode,
      course: r.courseName,
      mark: r.gradeLabel ?? '—',
      outcome: tr(`academicRecord.outcome.${r.outcome}`),
      points: formatPoints(r.points),
      attempted: String(r.unitsAttempted),
      earned: String(r.unitsEarned),
      note: r.gradeNote ? tr('academicRecord.record.sourceNote', { note: r.gradeNote }) : null,
      repeat: r.repeat,
    })),
  ]);
  const programs = [...new Set(input.programs ?? [])].filter((n) => n.trim() !== '');

  const current = groupByTerm(p.inProgress);

  const credentials = p.credentials.map((c) => {
    const s = shortfallOf(c);
    const lines = [
      tr('academicRecord.credential.units', { earned: c.unitsEarned, required: c.unitsRequired }),
      tr('academicRecord.credential.required', {
        met: c.requiredMet.length,
        total: c.requiredMet.length + c.requiredMissing.length,
      }),
      // Los TRES requisitos, también cuando se cumplen: créditos, obligatorios
      // y núcleo (cada grupo de la regla).
      ...c.groups.map((g) =>
        tr('academicRecord.credential.core', { met: g.met.length, min: g.minCourses }),
      ),
      ...(s.requiredMissing.length
        ? [tr('academicRecord.pdf.missingRequired', { courses: s.requiredMissing.join(', ') })]
        : []),
      ...s.groups.map((g) =>
        tr('academicRecord.pdf.missingGroup', {
          count: g.shortfall,
          courses: g.courseCodes.join(', '),
        }),
      ),
      ...(c.eligible
        ? []
        : [tr('academicRecord.credential.coursesMissing', { count: c.coursesMissing })]),
    ];
    return {
      name: c.name,
      status: tr(
        c.eligible ? 'academicRecord.credential.eligible' : 'academicRecord.credential.notYet',
      ),
      lines,
    };
  });

  const mismatch = graduationMismatch(p);
  const sourceGraduation = p.recordedGraduations.length
    ? {
        title: tr(student ? 'academicRecord.student.graduated' : 'academicRecord.source.graduated'),
        lines: [
          ...p.recordedGraduations.flatMap((g) => [
            ...(g.endedAt
              ? [
                  tr(
                    student
                      ? 'academicRecord.student.graduatedOn'
                      : 'academicRecord.source.graduatedOn',
                    { date: input.formatDay(g.endedAt) },
                  ),
                ]
              : []),
            ...(g.note ? [tr('academicRecord.source.note', { note: g.note })] : []),
          ]),
          ...(mismatch
            ? [tr('academicRecord.pdf.mismatch', { credential: mismatch.credentialName })]
            : []),
        ],
      }
    : undefined;

  return {
    title: tr('academicRecord.pdf.title'),
    watermark: tr('academicRecord.pdf.watermark'),
    draftNotice: tr('academicRecord.pdf.draftNotice'),
    tenantName: tenant.name,
    ...(logoUrl ? { logoUrl } : {}),
    ...(address ? { address } : {}),
    issuedOn: tr('academicRecord.pdf.issuedOn', { date: input.issuedOn }),
    student: {
      label: tr('academicRecord.pdf.student'),
      name: `${p.firstName} ${p.lastName}`.trim(),
      ...(p.studentCode
        ? { code: tr('academicRecord.pdf.studentCode', { code: p.studentCode }) }
        : {}),
      ...(programs.length
        ? { program: tr('academicRecord.pdf.program', { name: programs.join(', ') }) }
        : {}),
    },
    columns: {
      course: tr('academicRecord.record.columns.course'),
      mark: tr('academicRecord.pdf.colGrade'),
      points: tr('academicRecord.pdf.colPoints'),
      units: tr('academicRecord.pdf.colUnits'),
      attempted: tr('academicRecord.pdf.colAttempted'),
      earned: tr('academicRecord.pdf.colEarned'),
    },
    repeatLabel: tr('academicRecord.record.repeat'),
    table,
    ...(table.length === 0 ? { emptyRecord: tr('academicRecord.record.empty') } : {}),
    ...(current.length
      ? {
          inProgress: {
            title: tr('academicRecord.inProgress.title'),
            help: tr('academicRecord.inProgress.help'),
            terms: current.map((g) => ({
              name: g.termName,
              courses: g.rows.map((r) => `${r.courseCode} · ${r.courseName}`),
            })),
          },
        }
      : {}),
    totals: [
      {
        label: tr('academicRecord.pdf.effectiveUnits'),
        value: String(p.totals.unitsEarned),
        help: tr('academicRecord.summary.attempted', { count: p.totals.unitsAttempted }),
      },
      { label: tr('academicRecord.summary.coursesPassed'), value: String(p.totals.coursesPassed) },
      {
        label: tr('academicRecord.summary.gpa', { range: averageRange(toScale(p.scale)) }),
        value: formatGpa(p.totals.gpa),
        help: tr('academicRecord.summary.gpaHelp'),
      },
    ],
    credentialsTitle: tr('academicRecord.tabs.credential'),
    credentials,
    ...(sourceGraduation ? { sourceGraduation } : {}),
    pageLabel: (page, total) => tr('academicRecord.pdf.page', { page, total }),
  };
}
